import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Box, Button, Chip, Divider, IconButton, Link, List, ListItem, Paper, Stack, TextField, Typography } from '@mui/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import QuestionAnswerOutlined from '@mui/icons-material/QuestionAnswerOutlined'
import ThumbDown from '@mui/icons-material/ThumbDown'
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined'
import ThumbUp from '@mui/icons-material/ThumbUp'
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
  askAssistant,
  askAssistantNext,
  askAssistantText,
  ASSISTANT_TOPICS_KEY,
  fetchAssistantTopics,
  rateAssistantAnswer,
  type AssistantAnswer,
  type AssistantContext,
  type AssistantQuestionResult,
  type AssistantSuggestion,
} from '../api/assistant'
import { aiAvailabilityKey, fetchAiAvailability } from '../api/aiAvailability'
import { ASSISTANT_PENDING_KEY, takePending } from '../api/assistantPending'
import { fetchAllJobs } from '../api/jobs'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { VigaIcon } from '../components/VigaIcon'
import { VIGA } from '../components/viga'
import { t } from '../i18n'

/**
 * s63 / ADR-0033 fatia 1 (decisao de Marcelo 20/10/2026 20:40, "A1 A") -- "Pergunte ao sistema", NIVEL 1, sem IA.
 * O usuario escolhe o ASSUNTO e o CARGO; o servidor monta a resposta do cadastro e diz de onde veio (D2, D4).
 * Icone NEUTRO: a VIGA e so para IA (D9). Rota fixa /assistente, sem dado na URL (ADR-0024 D10.1).
 *
 * s64 / fatia 2 (Marcelo 21/10/2026 15:49, "F2 APROVADO ; F2b sim"): com a IA LIGADA para a empresa
 * (GET /api/ai/availability), aparece a caixa da pergunta ESCRITA (nivel 2); desligada -- ou se a leitura falhar --
 * a caixa nem aparece (F2b). A VIGA aparece SO na resposta que a IA entendeu (D9).
 *
 * s64 / fatia 2c (Marcelo 21/10/2026 17:42, "C1-C6 sim"): a tela vira CONVERSA -- a pergunta escrita aparece, a ordem
 * e cronologica (a mais nova embaixo), varias respostas por pergunta (C5), "Nova conversa" (C3) e o link para "Como a IA
 * trata seus dados" (C6). A tela lembra SO o ultimo assunto e o ultimo cargo respondidos (ids, C1) e manda isso com a
 * proxima pergunta; nada e guardado no navegador -- sair da pagina ou "Nova conversa" esquece.
 *
 * s64 / 2c-r1 (Marcelo 21/10/2026 19:40: "cliquei em unidades e depois voltei e a conversa tinha se apagado"): a conversa
 * passa a viver na MEMORIA da aba (cache do React Query, chave ASSISTANT_CONVERSATION_KEY), nao no componente: ir a outra
 * tela e voltar a mantem. Continua SEM navegador (nada em localStorage/sessionStorage: a resposta pode ter nome de pessoa).
 * Esquecem a conversa: "Nova conversa", "Sair" (queryClient.clear) e TROCAR DE EMPRESA (RequireSession apaga tudo que nao e
 * da sessao) -- a conversa de uma empresa nunca aparece na outra. Recarregar a pagina (F5) tambem esquece.
 *
 * s64 / 2e (Marcelo 21/10/2026 20:54, "vamos de A"): com a IA LIGADA a tela e "Pergunte à VIGA" e o botao "Perguntar à
 * VIGA" (V1); sem IA continua "Pergunte ao sistema" (V2). Cada resposta abre com a FRASE do servidor (R1) e a ultima
 * oferece as proximas perguntas como botoes, respondidas pelo cadastro SEM IA (R2).
 *
 * s64 / 2f (Marcelo 22/10/2026: "o layout em relacao as caixinhas mais parecido com o whatsapp ... a mesma estrutura",
 * W1/W2): conversa de MENSAGEIRO -- voce a direita, a VIGA a esquerda, hora em cada balao, separador "Hoje", caixa de
 * escrever FIXA embaixo, "VIGA esta pensando..." enquanto espera, boas-vindas da VIGA. Os menus ficam atras de
 * "Escolher pelo menu" (abrem sozinhos quando a VIGA pede para usa-los); sem IA, os menus continuam a tela principal.
 * A VEZ na conversa (``turn``) vai junto: o servidor varia o jeito de dizer (>= 5 por assunto). Cores e forma sao da
 * marca STRONGERH (nada do visual do WhatsApp, so a estrutura).
 *
 * s65 / 2g (Marcelo 22/10/2026 17:58, "nao e assim que estava imaginando"; 19:09 "APROVADO 2g ; G1 sim ; G2 A ; G3 A ;
 * G4 sim ; G5 A"): o balao da VIGA e SO a fala -- a frase e o FECHO de continuidade do servidor, sem linha-titulo (G1, G2);
 * a origem e a IA ficam num RODAPE pequeno, sempre visivel, e o (i) abre o detalhe completo (G3); as proximas perguntas e
 * "Abrir a ficha do cargo" viram CHIPS fora do balao, so na ultima fala da conversa (G4); o aviso e uma fala normal e as
 * candidatas viram chips que perguntam de novo pelo texto (G5 A).
 *
 * s67 / 2i (V3 + R2, Marcelo 25/10/2026 14:46 "APROVADO 2i"): a MESMA tela abre num PAINEL LATERAL (VigaDock, modo
 * "painel") -- sem Voltar, sem titulo e sem a introducao (o painel tem o proprio cabecalho); o link "Como a IA trata seus
 * dados" FICA (a frase do D10 continua a um clique). A pergunta PENDENTE deixada pela busca no topo
 * (ASSISTANT_PENDING_KEY) e lida-e-apagada num passo so e enviada pelo caminho do texto digitado -- so com a IA ligada.
 */
export const ASSISTANT_CONVERSATION_KEY = ['assistant-conversation'] as const
type Conversa = { itens: Item[]; contexto: AssistantContext | null }
const VAZIA: Conversa = { itens: [], contexto: null }

// s70 / polegar: ``queryId`` = o registro da pergunta (so a ULTIMA fala de cada pergunta escrita avaliavel o leva)
type Item =
  | { tipo: 'voce'; texto: string; em?: string }
  | { tipo: 'resposta'; r: AssistantAnswer; em?: string; queryId?: string }
  | { tipo: 'aviso'; m: AssistantQuestionResult; em?: string; queryId?: string }

// s70 / polegar (F1): "respondida", "ainda nao tenho" e "ainda nao sei" recebem avaliacao; conversa social e pergunta de
// volta ("de qual cargo?") nao
const AVALIAVEIS = new Set(['answered', 'no_module', 'unknown_topic'])

const agora = () => new Date().toISOString()
const hora = (em?: string) => (em ? new Date(em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '')

export function AssistantPage({ modo = 'pagina' }: { modo?: 'pagina' | 'painel' } = {}) {
  const painel = modo === 'painel'
  const navigate = useNavigate()
  const location = useLocation()
  const voltar = () => (location.key !== 'default' ? navigate(-1) : navigate('/'))
  const [topico, setTopico] = useState('')
  const [cargo, setCargo] = useState('')
  const [texto, setTexto] = useState('')
  const [menus, setMenus] = useState(false)
  const qc = useQueryClient()
  const conversa = useQuery({
    queryKey: ASSISTANT_CONVERSATION_KEY,
    queryFn: () => VAZIA,
    initialData: VAZIA,
    staleTime: Infinity,
    gcTime: Infinity,
  })
  const { itens, contexto } = conversa.data
  const mexe = (f: (c: Conversa) => Conversa) => qc.setQueryData<Conversa>(ASSISTANT_CONVERSATION_KEY, (c) => f(c ?? VAZIA))
  const setItens = (f: (anteriores: Item[]) => Item[]) => mexe((c) => ({ ...c, itens: f(c.itens) }))
  const setContexto = (novo: AssistantContext | null) => mexe((c) => ({ ...c, contexto: novo }))
  const fim = useRef<HTMLDivElement | null>(null)

  const topicos = useQuery({ queryKey: ASSISTANT_TOPICS_KEY, queryFn: fetchAssistantTopics })
  const cargos = useQuery({ queryKey: ['jobs', 'all'], queryFn: fetchAllJobs })
  const ia = useQuery({ queryKey: aiAvailabilityKey(), queryFn: fetchAiAvailability, retry: false })
  const comIa = ia.data === true
  // s67 / 2i: a pergunta deixada pela busca no topo (so o aviso de que existe; o texto e lido-e-apagado no efeito)
  const pendente = useQuery({
    queryKey: ASSISTANT_PENDING_KEY,
    queryFn: () => null as string | null,
    initialData: null as string | null,
    staleTime: Infinity,
    gcTime: Infinity,
  })
  const ativos = (cargos.data ?? [])
    .filter((j) => j.status === 'active')
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  // s64 / 2f: a VEZ na conversa = quantas vezes a VIGA ja falou (o servidor varia o jeito de dizer)
  const vez = itens.filter((it) => it.tipo !== 'voce').length

  const lembra = (respostas: AssistantAnswer[]) => {
    const ultima = respostas[respostas.length - 1]
    if (!ultima) return
    // s64 / 2d: a conversa pode continuar sobre uma PESSOA ou uma AREA ("e o chefe dela?") -- so o id vai junto
    if (ultima.subject_type === 'pessoa' && ultima.subject_id) setContexto({ topic: ultima.topic, person_id: ultima.subject_id })
    else if (ultima.subject_type === 'area' && ultima.subject_id) setContexto({ topic: ultima.topic, area_id: ultima.subject_id })
    else setContexto({ topic: ultima.topic, job_id: ultima.job_id })
  }
  const perguntar = useMutation({
    mutationFn: () => askAssistant(topico, cargo),
    onSuccess: (r) => {
      setItens((anteriores) => [...anteriores, { tipo: 'resposta', r, em: agora() }])
      lembra([r])
    },
  })
  const escrever = useMutation({
    mutationFn: (v: { texto: string; contexto: AssistantContext | null; vez: number }) =>
      askAssistantText(v.texto, v.contexto, v.vez),
    onMutate: (v) => {
      setItens((anteriores) => [...anteriores, { tipo: 'voce', texto: v.texto, em: agora() }])
      setTexto('')
    },
    onSuccess: (m) => {
      const qid = m.query_id && AVALIAVEIS.has(m.outcome) ? m.query_id : undefined
      if (m.answers.length > 0) {
        const n = m.answers.length
        setItens((anteriores) => [
          ...anteriores,
          ...m.answers.map((r, k): Item => ({ tipo: 'resposta', r, em: agora(), ...(k === n - 1 && qid ? { queryId: qid } : {}) })),
        ])
        lembra(m.answers)
      } else {
        setItens((anteriores) => [...anteriores, { tipo: 'aviso', m, em: agora(), ...(qid ? { queryId: qid } : {}) }])
      }
    },
  })
  const seguir = useMutation({
    mutationFn: (v: { topic: string; label: string; sobre: AssistantAnswer; vez: number }) =>
      askAssistantNext(v.topic, v.sobre, v.vez),
    onMutate: (v) => setItens((anteriores) => [...anteriores, { tipo: 'voce', texto: v.label, em: agora() }]),
    onSuccess: (r) => {
      setItens((anteriores) => [...anteriores, { tipo: 'resposta', r, em: agora() }])
      lembra([r])
    },
  })
  const pensando = escrever.isPending || seguir.isPending || perguntar.isPending
  useEffect(() => {
    if (!comIa || pendente.data === null) return
    const texto = takePending(qc)
    if (texto) escrever.mutate({ texto, contexto, vez })
    // so reage a chegada de uma pergunta pendente (ou a IA ficar sabida); o resto e lido na hora
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comIa, pendente.data])
  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: 'end' })
  }, [itens, pensando])

  const ultimo = itens[itens.length - 1]
  // W2: com IA os menus ficam atras do botao -- e abrem sozinhos quando a VIGA pede para usa-los
  // s66 / 2h (H2): "isso eu ainda nao tenho" (no_module) nao e pedido para usar os menus -- eles ficam fechados
  const pedeMenus = ultimo?.tipo === 'aviso' && ultimo.m.outcome !== 'small_talk' && ultimo.m.outcome !== 'no_module'
  const mostraMenus = !comIa || menus || pedeMenus
  const enviar = () => {
    const limpo = texto.trim()
    if (limpo && !escrever.isPending) escrever.mutate({ texto: limpo, contexto, vez })
  }
  // s65 / 2g (G5 A): a candidata escolhida no chip vira a pergunta seguinte, pelo mesmo caminho do texto (mascarado)
  const escolhe = (c: string) => {
    if (!escrever.isPending) escrever.mutate({ texto: c, contexto, vez })
  }
  const novaConversa = () => {
    qc.setQueryData<Conversa>(ASSISTANT_CONVERSATION_KEY, VAZIA)
    escrever.reset()
    perguntar.reset()
    seguir.reset()
  }

  const baloes = (
    <>
      {itens.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
          <Chip size="small" label={t.assistant.today} />
        </Box>
      )}
      {itens.map((it, i) =>
        it.tipo === 'voce' ? (
          <BalaoVoce key={i} texto={it.texto} em={it.em} />
        ) : it.tipo === 'aviso' ? (
          <Box key={i}>
            <BalaoViga em={it.em}>
              <Typography variant="body1">{it.m.message}</Typography>
            </BalaoViga>
            {it.queryId && <Polegar queryId={it.queryId} />}
            {i === itens.length - 1 && !pensando && it.m.candidates.length > 0 && (
              <Chips>
                {it.m.candidates.map((c) => (
                  <Chip key={c} label={c} variant="outlined" color="info" clickable onClick={() => escolhe(c)} />
                ))}
              </Chips>
            )}
          </Box>
        ) : (
          <Resposta
            key={i}
            r={it.r}
            em={it.em}
            queryId={it.queryId}
            abrir={() => navigate(`/cargos/${it.r.job_id}`)}
            ultima={i === itens.length - 1 && !pensando}
            onProxima={(s) => seguir.mutate({ topic: s.topic, label: s.label, sobre: it.r, vez })}
          />
        ),
      )}
      {pensando && (
        <BalaoViga>
          <Typography variant="body2" color="text.secondary">
            {t.assistant.thinking}
          </Typography>
        </BalaoViga>
      )}
      <div ref={fim} />
    </>
  )

  return (
    <Stack spacing={2} sx={{ maxWidth: 820 }}>
      {!painel && (
        <>
          <Box>
            <Button size="small" startIcon={<ArrowBack fontSize="small" />} onClick={voltar}>
              {t.assistant.back}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuestionAnswerOutlined color="primary" />
            <Typography variant="h1" sx={{ fontSize: '1.5rem' }}>
              {comIa ? t.assistant.titleAi : t.assistant.title}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ display: comIa ? { xs: 'none', sm: 'block' } : 'block' }}>
            {comIa ? t.assistant.introAi : t.assistant.intro}
          </Typography>
        </>
      )}
      {comIa && (
        <Box>
          <Link component={RouterLink} to="/ia-e-seus-dados" variant="body2">
            {t.assistant.aiDataLink}
          </Link>
        </Box>
      )}

      {comIa ? (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: painel ? 'calc(100vh - 140px)' : { xs: '70vh', md: 'calc(100vh - 320px)' },
            minHeight: 380,
            overflow: 'hidden',
          }}
        >
          <Stack spacing={1.25} aria-live="polite" sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'action.hover' }}>
            <BalaoViga>
              <Typography variant="body2">{t.assistant.welcome}</Typography>
            </BalaoViga>
            {baloes}
          </Stack>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              label={t.assistant.textLabel}
              helperText={t.assistant.textHelp}
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, 300))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') enviar()
              }}
              id="assistente-texto"
            />
            <Button
              variant="contained"
              aria-label={t.assistant.send}
              startIcon={<VigaIcon src={VIGA.icone} size={20} />}
              disabled={!texto.trim() || escrever.isPending}
              onClick={enviar}
              sx={{
                flexShrink: 0,
                minWidth: painel ? 48 : { xs: 48, sm: 64 },
                '& .MuiButton-startIcon': { mr: painel ? 0 : { xs: 0, sm: 1 } },
              }}
            >
              {/* s67 / 2i: no painel (~400 px) o botao fica so com a VIGA, como no celular -- a caixa de escrever ganha o espaco */}
              <Box component="span" sx={{ display: painel ? 'none' : { xs: 'none', sm: 'inline' } }}>
                {t.assistant.send}
              </Box>
            </Button>
          </Box>
          <Box sx={{ px: 1.5, pb: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => setMenus((m) => !m)}>
              {mostraMenus ? t.assistant.closeMenus : t.assistant.openMenus}
            </Button>
            {itens.length > 0 && (
              <Button size="small" variant="outlined" onClick={novaConversa}>
                {t.assistant.newChat}
              </Button>
            )}
          </Box>
        </Paper>
      ) : (
        itens.length > 0 && <Stack spacing={1.25} aria-live="polite">{baloes}</Stack>
      )}
      {escrever.isError && <ErrorState error={escrever.error} />}

      {mostraMenus &&
        (topicos.isError || cargos.isError ? (
          <ErrorState error={topicos.error ?? cargos.error} />
        ) : topicos.isPending || cargos.isPending ? (
          <LoadingState />
        ) : (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {comIa && (
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
                {t.assistant.orMenus}
              </Typography>
            )}
            <Stack spacing={2} direction={painel ? 'column' : { xs: 'column', sm: 'row' }} alignItems={painel ? undefined : { sm: 'flex-end' }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t.assistant.topicLabel}
                value={topico}
                onChange={(e) => setTopico(e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                id="assistente-assunto"
              >
                <option value="">{t.assistant.choose}</option>
                {(topicos.data ?? []).map((tp) => (
                  <option key={tp.id} value={tp.id}>
                    {tp.question}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label={t.assistant.jobLabel}
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                id="assistente-cargo"
              >
                <option value="">{t.assistant.choose}</option>
                {ativos.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.name}
                  </option>
                ))}
              </TextField>
              <Button
                variant="contained"
                disabled={!topico || !cargo || perguntar.isPending}
                onClick={() => perguntar.mutate()}
                sx={{ flexShrink: 0 }}
              >
                {t.assistant.ask}
              </Button>
            </Stack>
          </Paper>
        ))}

      {perguntar.isError && <ErrorState error={perguntar.error} />}
      {seguir.isError && <ErrorState error={seguir.error} />}

      {!comIa && itens.length > 0 && (
        <Box>
          <Button size="small" variant="outlined" onClick={novaConversa}>
            {t.assistant.newChat}
          </Button>
        </Box>
      )}
    </Stack>
  )
}

/** s64 / 2f: balao de quem pergunta -- a direita. */
function BalaoVoce({ texto, em }: { texto: string; em?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Paper
        elevation={0}
        sx={{ px: 1.75, py: 1, maxWidth: '80%', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '14px 14px 4px 14px' }}
      >
        <Typography variant="caption" component="p" sx={{ opacity: 0.8 }}>
          {t.assistant.you}
        </Typography>
        <Typography variant="body2">{texto}</Typography>
        {em && (
          <Typography variant="caption" component="p" sx={{ opacity: 0.75, textAlign: 'right', mt: 0.25 }}>
            {hora(em)}
          </Typography>
        )}
      </Paper>
    </Box>
  )
}

/** s64 / 2f: balao da VIGA -- a esquerda, com o desenho da VIGA (decorativo). */
function BalaoViga({ children, em }: { children: ReactNode; em?: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'flex-end' }}>
      <VigaIcon src={VIGA.icone} size={24} />
      <Paper
        variant="outlined"
        sx={{ px: 1.75, py: 1, maxWidth: '85%', bgcolor: 'background.paper', borderRadius: '14px 14px 14px 4px' }}
      >
        {children}
        {em && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'right', mt: 0.25 }}>
            {hora(em)}
          </Typography>
        )}
      </Paper>
    </Box>
  )
}

/** s65 / 2g: a linha de chips logo abaixo do balao da VIGA (resposta rapida, como no mensageiro). */
function Chips({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75, ml: 4 }}>{children}</Box>
}

const minuscula = (x: string) => (x ? x.charAt(0).toLocaleLowerCase('pt-BR') + x.slice(1) : x)

function Resposta({
  r,
  em,
  queryId,
  abrir,
  ultima,
  onProxima,
}: {
  r: AssistantAnswer
  em?: string
  queryId?: string
  abrir: () => void
  ultima: boolean
  onProxima: (s: AssistantSuggestion) => void
}) {
  const [detalhe, setDetalhe] = useState(false)
  const linhas = r.show_lines === false ? [] : r.lines.slice(r.lines_from ?? 0)
  const proximas = ultima ? (r.suggestions ?? []) : []
  const ficha = ultima && r.can_open_job
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'flex-end' }}>
        <VigaIcon src={VIGA.icone} size={24} />
        <Paper
          variant="outlined"
          component="section"
          sx={{ px: 1.75, pt: 1.25, pb: 0.5, maxWidth: '85%', bgcolor: 'background.paper', borderRadius: '14px 14px 14px 4px' }}
        >
          {/* s66 / 2h (H1): na 1a resposta da conversa a VIGA chama pelo nome */}
          {(r.phrase_named ?? r.phrase) && <Typography variant="body1">{r.phrase_named ?? r.phrase}</Typography>}
          {linhas.length > 0 && (
            <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2.5 }}>
              {linhas.map((l, k) => (
                <ListItem key={k} disableGutters sx={{ py: 0.125, display: 'list-item' }}>
                  <Typography variant="body1">{l}</Typography>
                </ListItem>
              ))}
            </List>
          )}
          {r.people && r.people.length > 0 && (
            <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2.5 }}>
              {r.people.map((p) => (
                <ListItem key={p.id} disableGutters sx={{ py: 0, display: 'list-item' }}>
                  <Typography variant="body1">{p.name}</Typography>
                </ListItem>
              ))}
            </List>
          )}
          {r.closing && <Typography variant="body1">{r.closing}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
            {r.level === 2 && <VigaIcon src={VIGA.icone} size={16} testId="viga-n2" />}
            <Typography variant="caption" color="text.secondary">
              {`${t.assistant.sourceVia} ${minuscula(r.source)}${em ? ` · ${hora(em)}` : ''}`}
            </Typography>
            <IconButton size="small" aria-label={t.assistant.sourceInfo} aria-expanded={detalhe} onClick={() => setDetalhe((d) => !d)}>
              <InfoOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          {detalhe && (
            <Box sx={{ textAlign: 'right', pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" component="p">
                {`${t.assistant.source} ${r.source}`}
              </Typography>
              {r.level === 2 && (
                <Typography variant="caption" color="text.secondary" component="p">
                  {t.assistant.aiBadge}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>
      {queryId && <Polegar queryId={queryId} />}
      {(proximas.length > 0 || ficha) && (
        <Chips>
          {proximas.map((s) => (
            <Chip key={s.topic} label={s.label} variant="outlined" color="info" clickable onClick={() => onProxima(s)} />
          ))}
          {ficha && <Chip label={t.assistant.openJob} variant="outlined" color="primary" clickable onClick={abrir} />}
        </Chips>
      )}
    </Box>
  )
}

/**
 * s70 / polegar (ADR-0036 L2; Marcelo 30/10/2026 19:55: "APROVADO polegar ; F1-F6 recomendado").
 * Conferencia (cc): Attio -- polegares cinza e pequenos abaixo da resposta; Stripe -- sem janela, agradece numa linha;
 * Rippling -- nada obrigatorio; WhatsApp -- a reacao fica marcada e o outro clique troca (F3).
 * F5: sempre visiveis (no celular nao ha "passar o mouse"). 👎 abre "o que faltou?" OPCIONAL (Enviar / Pular).
 */
function Polegar({ queryId }: { queryId: string }) {
  const [voto, setVoto] = useState<'up' | 'down' | null>(null)
  const [faltou, setFaltou] = useState('')
  const [caixa, setCaixa] = useState(false)
  const [obrigado, setObrigado] = useState(false)
  const avaliar = useMutation({
    mutationFn: (v: { rating: 'up' | 'down'; missing?: string }) => rateAssistantAnswer(queryId, v.rating, v.missing),
  })
  const clica = (rating: 'up' | 'down') => {
    setVoto(rating)
    setObrigado(rating === 'up')
    setCaixa(rating === 'down')
    avaliar.mutate({ rating })
  }
  const envia = () => {
    avaliar.mutate({ rating: 'down', missing: faltou })
    setCaixa(false)
    setObrigado(true)
  }
  const pula = () => {
    setCaixa(false)
    setObrigado(true)
  }
  return (
    <Box sx={{ ml: 4, mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <IconButton size="small" aria-label={t.assistant.rateUp} aria-pressed={voto === 'up'} onClick={() => clica('up')} sx={{ color: voto === 'up' ? 'primary.main' : 'text.disabled' }}>
          {voto === 'up' ? <ThumbUp sx={{ fontSize: 16 }} /> : <ThumbUpOutlined sx={{ fontSize: 16 }} />}
        </IconButton>
        <IconButton size="small" aria-label={t.assistant.rateDown} aria-pressed={voto === 'down'} onClick={() => clica('down')} sx={{ color: voto === 'down' ? 'primary.main' : 'text.disabled' }}>
          {voto === 'down' ? <ThumbDown sx={{ fontSize: 16 }} /> : <ThumbDownOutlined sx={{ fontSize: 16 }} />}
        </IconButton>
        {obrigado && (
          <Typography variant="caption" color="text.secondary">
            {t.assistant.rateThanks}
          </Typography>
        )}
      </Box>
      {caixa && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 0.5, maxWidth: 420 }}>
          <TextField
            size="small"
            fullWidth
            label={t.assistant.rateMissingLabel}
            value={faltou}
            onChange={(e) => setFaltou(e.target.value.slice(0, 280))}
            inputProps={{ maxLength: 280 }}
          />
          <Button size="small" variant="outlined" onClick={envia} disabled={!faltou.trim()}>
            {t.assistant.rateSend}
          </Button>
          <Button size="small" onClick={pula}>
            {t.assistant.rateSkip}
          </Button>
        </Box>
      )}
    </Box>
  )
}
